import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { QuestionMarkCircleIcon as SolidQuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import CT from '@marketplace-ai/tags';
import classNames from 'classnames';
import { useState } from 'react';
import { NavLink, useMatch } from 'react-router';
import type { SiteBrand } from '~/config/branding';
import { inAppFaqHref } from '../siteNavigation';
import { SupportRequestDialog } from './SupportRequestDialog';

const TAGS = CT.layout.helpMenu;

// HeaderBrandMenu's item style. The explicit width (not w-full) keeps the mx-1.5 inset on both sides
// for the button, which does not stretch like the link does.
const ITEM_CLASS =
  'block w-[calc(100%-12px)] mx-1.5 px-2.5 py-1.5 rounded-md text-left text-[13px] text-grey-200 cursor-pointer data-[focus]:bg-grey-900/50';

interface Props {
  brand: SiteBrand;
  /**
   * Offer "Contact support". The signed-in shells turn it off while an employer is impersonating a
   * provider: the request would go out under the employer's own account while the screen shows the
   * provider's vault. [MCV-1780]
   */
  canContactSupport?: boolean;
}

/**
 * The ? button in the signed-in top bars: a small menu with the in-app FAQ and a "Contact support"
 * form, the same for every role. [MCV-1780] (It was a plain FAQ link, MCV-1724.)
 *
 * - "Help & FAQs" is a same-tab router link, with no target or rel. The destination is an app
 *   route, not a public page, so ApplicationBoundary keeps the signed-in app mounted. NavLink
 *   compares the pathname only, so `/vault/faq#any-section` still carries aria-current="page";
 *   the trigger's icon fills in as the "you are here" state.
 * - `modal={false}` on the panel. The default (true) writes an inline `overflow: hidden` on <html>
 *   while the panel is open, which shifts the fixed header by the scrollbar width (MCV-1563, as
 *   in HeaderBrandMenu).
 * - Same chrome as the notification bell beside it (32x32 as a flex child, no `relative` since
 *   there is no badge). It must NOT carry `data-notification-toggle`: that marks the bell for the
 *   alerts popover's click-away guard.
 * - The accessible name must not be "Toggle notifications" (e2e selects the bell by that exact
 *   label).
 */
export const HeaderHelpMenu = ({ brand, canContactSupport = true }: Props) => {
  const faqHref = inAppFaqHref(brand);
  const onFaq = useMatch({ path: faqHref, end: false }) !== null;
  const [contactOpen, setContactOpen] = useState(false);
  const Icon = onFaq ? SolidQuestionMarkCircleIcon : QuestionMarkCircleIcon;

  return (
    <>
      <Menu as="div" className="relative inline-block text-left">
        <MenuButton
          aria-label="Help"
          title="Help"
          data-cy={TAGS.trigger}
          className={classNames(
            'block p-1.5 rounded-control transition-colors hover:bg-grey-900/50 cursor-pointer',
            onFaq && 'bg-grey-900/50'
          )}
        >
          <Icon aria-hidden="true" className="size-5 text-grey-400" />
        </MenuButton>
        <MenuItems
          modal={false}
          anchor="bottom end"
          className="w-[200px] bg-white rounded-lg shadow-lg border border-grey-900 py-2 z-50"
        >
          <MenuItem>
            <NavLink to={faqHref} data-cy={TAGS.faqItem} className={ITEM_CLASS}>
              Help & FAQs
            </NavLink>
          </MenuItem>
          {canContactSupport && (
            <MenuItem>
              <button
                type="button"
                data-cy={TAGS.contactSupportItem}
                className={ITEM_CLASS}
                onClick={() => setContactOpen(true)}
              >
                Contact support
              </button>
            </MenuItem>
          )}
        </MenuItems>
      </Menu>
      {canContactSupport && (
        <SupportRequestDialog
          open={contactOpen}
          brand={brand}
          onClose={() => setContactOpen(false)}
        />
      )}
    </>
  );
};
